/**
 * Emoji rendering configuration.
 *
 * Controls whether emojis are rendered as native unicode characters or as
 * vendor-specific images (e.g. Google emojis). This is framework-agnostic
 * so it can be used in both Angular components and plain utility functions
 * like Quill blots and message helpers.
 *
 * Non-native emojis use a single spritesheet (same one emoji-mart loads),
 * so there is only one image request regardless of how many emojis are displayed.
 *
 * Sprite positioning uses data-sx / data-sy attributes with CSS attribute
 * selectors (background-position-x / background-position-y) so that no inline
 * styles are needed. This keeps Angular's HTML sanitizer happy.
 */

import emojiDatasource from 'emoji-datasource-google/emoji.json';
import { version as emojiDatasourceVersion } from 'emoji-datasource-google/package.json';

interface EmojiDatasourceEntry {
  unified: string;
  short_name: string;
  short_names: string[];
  sheet_x: number;
  sheet_y: number;
  skin_variations?: Record<string, { unified: string; sheet_x: number; sheet_y: number }>;
}

/** Platforms that should use vendor emoji images instead of native. Add 'linux' or 'mac' here to test locally. */
const NON_NATIVE_EMOJI_PLATFORMS: string[] = ['windows'];

/** The emoji vendor set to use on non-native platforms. */
export const EMOJI_SET: 'apple' | 'google' | 'twitter' | 'facebook' = 'google';

const SHEET_SIZE = 64;

/**
 * Spritesheet URL pinned to the installed emoji-datasource-google version. Used only by
 * our in-app sprite renderer (message bodies, reactions, inline). The emoji-mart picker
 * uses its own bundled spritesheet URL since its `@emoji-mart/data` positions are tied
 * to a specific emoji-datasource version.
 */
const SPRITESHEET_URL = `https://cdn.jsdelivr.net/npm/emoji-datasource-${EMOJI_SET}@${emojiDatasourceVersion}/img/${EMOJI_SET}/sheets-256/${SHEET_SIZE}.png`;

function detectOS(): string {
  const userAgent = navigator.userAgent;
  if (userAgent.indexOf('Win') !== -1) {
    return 'windows';
  } else if (userAgent.indexOf('Mac') !== -1) {
    return 'mac';
  } else if (userAgent.indexOf('Linux') !== -1) {
    return 'linux';
  }

  return 'unknown';
}

/** Whether emojis should be rendered as native unicode characters on the current platform. */
export const USE_NATIVE_EMOJI = !NON_NATIVE_EMOJI_PLATFORMS.includes(detectOS());

/** Sheet coordinates for positioning a sprite. */
export interface SpritePosition {
  sx: number;
  sy: number;
}

interface InternalEmojiData {
  shortName: string;
  unified: string;
  sheet: [number, number];
  skinVariations?: Array<{ unified: string; sheet: [number, number] }>;
}

/** Lookup map from emoji shortName (id) to its data (includes sheet position). */
const emojiById = new Map<string, InternalEmojiData>();

/** Lookup map from emoji unified code to its data. */
const emojiByUnified = new Map<string, InternalEmojiData>();

/** Reverse lookup map from sprite position "sx,sy" to emoji data (for paste recovery). */
const emojiBySpritePos = new Map<string, InternalEmojiData>();

let maxSheetX = 0;
let maxSheetY = 0;

// Always populate lookup maps (needed for paste recovery even on native platforms)
for (const raw of emojiDatasource as unknown as EmojiDatasourceEntry[]) {
  const emoji: InternalEmojiData = {
    shortName: raw.short_name,
    unified: raw.unified,
    sheet: [raw.sheet_x, raw.sheet_y],
    skinVariations: raw.skin_variations
      ? Object.values(raw.skin_variations).map(v => ({
          unified: v.unified,
          sheet: [v.sheet_x, v.sheet_y] as [number, number],
        }))
      : undefined,
  };

  emojiById.set(emoji.shortName, emoji);

  // Also index alternate short names (e.g. "raised_hand" → "hand")
  for (const altName of raw.short_names) {
    if (altName !== emoji.shortName) {
      emojiById.set(altName, emoji);
    }
  }

  emojiByUnified.set(emoji.unified, emoji);
  emojiBySpritePos.set(`${emoji.sheet[0]},${emoji.sheet[1]}`, emoji);

  maxSheetX = Math.max(maxSheetX, raw.sheet_x);
  maxSheetY = Math.max(maxSheetY, raw.sheet_y);

  if (emoji.skinVariations) {
    for (const variation of emoji.skinVariations) {
      const varData: InternalEmojiData = {
        ...emoji,
        sheet: variation.sheet,
        unified: variation.unified,
      };

      emojiByUnified.set(variation.unified, varData);
      emojiBySpritePos.set(`${variation.sheet[0]},${variation.sheet[1]}`, varData);

      maxSheetX = Math.max(maxSheetX, variation.sheet[0]);
      maxSheetY = Math.max(maxSheetY, variation.sheet[1]);
    }
  }
}

/**
 * Spritesheet grid size, derived from the loaded emoji-datasource. The sheet is always
 * square and sized to `max(sheet_x, sheet_y) + 1`, so this stays correct across
 * emoji-datasource-google version bumps.
 */
const SHEET_COLUMNS = Math.max(maxSheetX, maxSheetY) + 1;
const SHEET_ROWS = SHEET_COLUMNS;

if (!USE_NATIVE_EMOJI) {
  injectSpriteStylesheet();
}

/**
 * Injects a <style> element with CSS rules for sprite positioning.
 * Uses .sx-N / .sy-N class selectors with background-position-x / background-position-y,
 * keeping the total rule count to SHEET_COLUMNS + SHEET_ROWS (~121) instead of one per emoji (~3000+).
 * Classes are used instead of data attributes because Angular's HTML sanitizer preserves classes
 * but strips data-* attributes from [innerHTML] bindings.
 */
function injectSpriteStylesheet() {
  const multiply = 100 / (SHEET_COLUMNS - 1);
  const rules: string[] = [];

  // Shared base rule
  rules.push(
    `.mc-ql-emoji-sprite { display: inline-block; width: 22px; height: 22px; background-image: url(${SPRITESHEET_URL}); background-size: ${100 * SHEET_COLUMNS}% ${100 * SHEET_ROWS}%; vertical-align: middle; }`,
  );

  // X-axis rules (columns)
  for (let x = 0; x < SHEET_COLUMNS; x++) {
    rules.push(`.mc-ql-emoji-sprite.sx-${x} { background-position-x: ${multiply * x}%; }`);
  }

  // Y-axis rules (rows)
  for (let y = 0; y < SHEET_ROWS; y++) {
    rules.push(`.mc-ql-emoji-sprite.sy-${y} { background-position-y: ${multiply * y}%; }`);
  }

  const style = document.createElement('style');
  style.textContent = rules.join('\n');
  document.head.appendChild(style);
}

/**
 * Converts a native emoji character to its unified hex code.
 * E.g. '😀' → '1F600', '👍🏽' → '1F44D-1F3FD'
 */
export function nativeToUnified(native: string): string {
  if (!native) {
    return '';
  }

  return [...native]
    .map(char => char.codePointAt(0)?.toString(16).toUpperCase().padStart(4, '0'))
    .join('-');
}

/**
 * Returns the spritesheet coordinates for an emoji by its ID (shortName).
 * Respects the user's skin tone preference from emoji-mart (stored in localStorage).
 */
export function getEmojiSpritePosition(emojiId: string): SpritePosition | null {
  const data = emojiById.get(emojiId);
  if (!data) {
    return null;
  }

  const skinTone = getSelectedSkinTone();
  if (skinTone > 1 && data.skinVariations) {
    const variation = data.skinVariations[skinTone - 2];
    if (variation) {
      return { sx: variation.sheet[0], sy: variation.sheet[1] };
    }
  }

  return { sx: data.sheet[0], sy: data.sheet[1] };
}

/** Reads the user's selected skin tone from emoji-mart's localStorage. Returns 1-6. */
function getSelectedSkinTone(): number {
  const stored = localStorage.getItem('emoji-mart.skin');
  return stored ? parseInt(stored, 10) : 1;
}

/** Converts a unified hex code to a native emoji string. E.g. '1F44D-1F3FD' → '👍🏽' */
function unifiedToNative(unified: string): string {
  return String.fromCodePoint(...unified.split('-').map(u => parseInt(u, 16)));
}

/**
 * Returns the native emoji character with the user's preferred skin tone applied.
 * If the emoji doesn't support skin tones or skin tone is default, returns the base native.
 */
export function getSkinTonedNative(emojiId: string, baseNative: string): string {
  const skinTone = getSelectedSkinTone();
  if (skinTone <= 1) {
    return baseNative;
  }

  const data = emojiById.get(emojiId);
  if (!data?.skinVariations) {
    return baseNative;
  }

  const variation = data.skinVariations[skinTone - 2];
  if (!variation) {
    return baseNative;
  }

  return unifiedToNative(variation.unified);
}

/**
 * Parses an emoji name into its base ID and optional skin tone.
 * Strips surrounding colons and splits a `::skin-tone-N` suffix, e.g.
 * `:walking::skin-tone-4:` → `{ baseId: 'walking', skinTone: 4 }`.
 */
export function parseEmojiId(emojiName: string): {
  baseId: string;
  skinTone?: 1 | 2 | 3 | 4 | 5 | 6;
} {
  const id = emojiName.replace(/^:/, '').replace(/:$/, '');
  const skinMatch = id.match(/^(.+)::skin-tone-([1-6])$/);

  if (skinMatch) {
    return { baseId: skinMatch[1], skinTone: parseInt(skinMatch[2], 10) as 1 | 2 | 3 | 4 | 5 | 6 };
  }

  return { baseId: id, skinTone: undefined };
}

/**
 * Returns the native emoji character for an emoji by its ID (shortName),
 * with an optional explicit skin tone (1-6).
 * If no skin tone is given, returns the base native character.
 */
export function getEmojiNativeById(emojiId: string, skinTone?: number): string | null {
  const data = emojiById.get(emojiId);

  if (!data) {
    return null;
  }

  if (skinTone && skinTone > 1 && data.skinVariations) {
    const variation = data.skinVariations[skinTone - 2];

    if (variation) {
      return unifiedToNative(variation.unified);
    }
  }

  return unifiedToNative(data.unified);
}

/**
 * Returns the spritesheet coordinates for an emoji by its ID with an explicit skin tone.
 * Unlike getEmojiSpritePosition which uses the user's localStorage preference,
 * this accepts the skin tone directly.
 */
export function getEmojiSpritePositionWithSkin(
  emojiId: string,
  skinTone?: number,
): SpritePosition | null {
  const data = emojiById.get(emojiId);

  if (!data) {
    return null;
  }

  if (skinTone && skinTone > 1 && data.skinVariations) {
    const variation = data.skinVariations[skinTone - 2];

    if (variation) {
      return { sx: variation.sheet[0], sy: variation.sheet[1] };
    }
  }

  return { sx: data.sheet[0], sy: data.sheet[1] };
}

/**
 * Returns the spritesheet coordinates for an emoji by its unified code.
 * Falls back to trying with/without FE0F variation selector.
 */
export function getEmojiSpritePositionByUnified(unified: string): SpritePosition | null {
  let data = emojiByUnified.get(unified);

  if (!data) {
    const withoutFe0f = unified.replace(/-FE0F/g, '');
    data = emojiByUnified.get(withoutFe0f);
  }

  if (!data) {
    data = emojiByUnified.get(unified + '-FE0F');
  }

  if (!data) {
    return null;
  }

  return { sx: data.sheet[0], sy: data.sheet[1] };
}

/** Returns the CSS class string for positioning an emoji sprite (e.g. "mc-ql-emoji-sprite sx-5 sy-10"). */
export function emojiSpriteClasses(pos: SpritePosition | null): string {
  return pos ? `mc-ql-emoji-sprite sx-${pos.sx} sy-${pos.sy}` : 'mc-ql-emoji-sprite';
}

/**
 * Recovers emoji id and native value from sprite position classes.
 * Used when pasting emojis from messages where data attributes are stripped by Angular's sanitizer.
 */
export function getEmojiFromSpritePosition(
  sx: number,
  sy: number,
): { id: string; native: string; colons: string } | null {
  const data = emojiBySpritePos.get(`${sx},${sy}`);
  if (!data) {
    return null;
  }

  const skinSuffix = detectSkinToneSuffix(data.unified);

  return {
    id: data.shortName,
    native: String.fromCodePoint(...data.unified.split('-').map(u => parseInt(u, 16))),
    colons: `:${data.shortName}:${skinSuffix}`,
  };
}

/** Skin tone modifier code points (1F3FB–1F3FF) map to emoji-mart skin tones 2–6. */
const SKIN_TONE_MODIFIERS: Record<string, number> = {
  '1F3FB': 2,
  '1F3FC': 3,
  '1F3FD': 4,
  '1F3FE': 5,
  '1F3FF': 6,
};

/**
 * Detects the skin tone from a unified code and returns the skin-tone suffix for colons notation.
 * E.g. unified "1F44D-1F3FD" → "::skin-tone-4:"
 */
function detectSkinToneSuffix(unified: string): string {
  const parts = unified.split('-');

  for (const part of parts) {
    const tone = SKIN_TONE_MODIFIERS[part];

    if (tone) {
      return `:skin-tone-${tone}:`;
    }
  }

  return '';
}

/**
 * Looks up emoji info by its native character.
 * Returns id (shortName), and colons with skin tone suffix if applicable.
 * Used when pasting native emojis where data attributes were stripped by Angular's sanitizer.
 */
export function getEmojiInfoByNative(native: string): { id: string; colons: string } | null {
  const unified = nativeToUnified(native);
  const data =
    emojiByUnified.get(unified) ??
    emojiByUnified.get(unified.replace(/-FE0F/g, '')) ??
    emojiByUnified.get(unified + '-FE0F');

  if (!data) {
    return null;
  }

  const skinSuffix = detectSkinToneSuffix(unified);

  return {
    id: data.shortName,
    colons: `:${data.shortName}:${skinSuffix}`,
  };
}

/** Creates a positioned sprite span element for the given emoji ID. */
export function createEmojiSpriteElement(emojiId: string): HTMLSpanElement {
  const span = document.createElement('span');
  span.className = emojiSpriteClasses(getEmojiSpritePosition(emojiId));
  return span;
}

/** Creates a positioned sprite span element from a unified code. */
export function createEmojiSpriteElementByUnified(unified: string): HTMLSpanElement {
  const span = document.createElement('span');
  span.className = emojiSpriteClasses(getEmojiSpritePositionByUnified(unified));
  return span;
}
